import { type FormEvent, useMemo, useState } from 'react'
import type { Label, LabelCategory, LabelStatus } from '../../../api/labels'
import { Button, FormField, Modal, ModalActions } from '../../../components/ui'
import { labelCategoryLabels, labelCategoryOptions, labelStatusLabels, labelStatusOptions } from '../labelDisplay'
import styles from './LabelDialog.module.css'

type LabelDialogProps = {
  label: Label
  onClose: () => void
  onSave: (input: {
    id: string
    category: LabelCategory
    name: string
    slug: string
    description: string | null
    aliases: string[]
    status: LabelStatus
  }) => Promise<void>
}

const slugify = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const parseAliases = (value: string): string[] =>
  Array.from(new Set(
    value
      .split('\n')
      .map((alias) => alias.trim())
      .filter(Boolean)
  ))

export const LabelDialog = (props: LabelDialogProps) => {
  const [category, setCategory] = useState<LabelCategory>(props.label.category)
  const [name, setName] = useState(props.label.name)
  const [slug, setSlug] = useState(props.label.slug)
  const [description, setDescription] = useState(props.label.description ?? '')
  const [aliases, setAliases] = useState(props.label.aliases.join('\n'))
  const [status, setStatus] = useState<LabelStatus>(props.label.status)
  const [saving, setSaving] = useState(false)
  const computedSlug = useMemo(() => slug || slugify(name), [name, slug])

  const save = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setSaving(true)
    try {
      await props.onSave({
        id: props.label.id,
        category,
        name,
        slug: computedSlug,
        description: description.trim() || null,
        aliases: parseAliases(aliases),
        status
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      ariaLabelledBy="label-editor-title"
      bodyClassName={styles.body ?? ''}
      mobilePresentation="sheet"
      onClose={props.onClose}
      onSubmit={(event) => void save(event)}
      title={<h2 id="label-editor-title">{props.label.id ? 'Edit label' : 'Create label'}</h2>}
    >
        <div className={styles.formGrid ?? ''}>
          <FormField label="Category">
            <select className="form-input" onChange={(event) => setCategory(event.target.value as LabelCategory)} value={category}>
              {labelCategoryOptions.map((option) => (
                <option key={option} value={option}>{labelCategoryLabels[option]}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Status">
            <select className="form-input" onChange={(event) => setStatus(event.target.value as LabelStatus)} value={status}>
              {labelStatusOptions.map((option) => (
                <option key={option} value={option}>{labelStatusLabels[option]}</option>
              ))}
            </select>
          </FormField>
        </div>
        <FormField label="Name">
          <input className="form-input" onChange={(event) => setName(event.target.value)} value={name} />
        </FormField>
        <FormField label="Slug">
          <input className="form-input" onChange={(event) => setSlug(event.target.value)} placeholder={slugify(name)} value={slug} />
        </FormField>
        <FormField label="Description">
          <textarea className="form-input" onChange={(event) => setDescription(event.target.value)} value={description} />
        </FormField>
        <FormField label="Aliases">
          <textarea
            className="form-input"
            onChange={(event) => setAliases(event.target.value)}
            placeholder="One alias per line"
            value={aliases}
          />
        </FormField>
        {props.label.id && (
          <p className="muted">
            Used by {props.label.track_count} approved track label(s).
          </p>
        )}
        <ModalActions>
          <Button onClick={props.onClose} variant="secondary">Cancel</Button>
          <Button disabled={saving || !name.trim()} type="submit" variant="primary">
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </ModalActions>
    </Modal>
  )
}
